import { dataMixed } from "api/data";
import { DataReqBody } from "api/types/data";
import { Device } from "api/types/device";
import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";

import { formatDate, formatDateToZ } from "util/datelib";
import Combobox from "widgets/shared/inputs/combobox";
import DatePickerField from "widgets/shared/inputs/datePicker";
import PropagateLoader from "react-spinners/PropagateLoader";
import { t } from "i18next";
import { DeviceConfiguration } from "models/devices/configuration";
import { getChartColors } from "util/device";
import DeviceCard from "widgets/cards/device";
import NoDataSvg from "assets/svg/no-data.svg";
import { ChartDataset } from "widgets/charts/models";
import LineChart from "widgets/charts/line";
import ChartButton from "widgets/controls/buttons/chart";
import { colorPalette500Css } from "models/constants/colors";
import { TimeSeriesChartDropdown } from "models/dropdowns/timeSeriesChart";
import GenericButton from "widgets/controls/buttons/generic";

// Define the props for the Role component
interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
  height?: number;
}

interface ChartDataPoint {
  timestamp: string;
  [key: string]: any;
}

interface Dataset {
  label: string;
  unit?: string;
  data: (number | null)[];
  backgroundColor: string | string[];
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  borderSkipped?: boolean;
  pointRadius?: number;
  tension?: number;
  fill?: boolean;
  yAxisID?: string;
  borderDash?: number[];
}

const initialChartData: ChartDataset = {
  labels: [],
  datasets: [],
};

const MultiLine: React.FC<GeneralProps> = ({ device, config, height }) => {
  const { color0, color1 } = getChartColors(config || undefined);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [visibleDatasets, setVisibleDatasets] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedLeftUnit, setSelectedLeftUnit] = useState<string | null>(null);
  const [selectedRightUnit, setSelectedRightUnit] = useState<string | null>(
    null
  );

  const [chartData, setChartData] = useState<ChartDataset>(initialChartData);
  const [dataSets, setDataSets] = useState<Dataset[]>([]);
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [allUnits, setAllUnits] = useState<string[]>([]);
  const [date, setDate] = useState<any>(new Date());
  const [filter, setFilter] = useState<any>({
    calendar: "year",
    xFormat: "month",
    currentBucket: "day",
  });

  const setXFormat = (filter: string) => {
    if (filter === "month") {
      setFilter({ calendar: filter, xFormat: "day", currentBucket: "hour" });
    } else if (filter === "year") {
      setFilter({ calendar: filter, xFormat: "month", currentBucket: "day" });
    } else {
      setFilter({ calendar: filter, xFormat: "year", currentBucket: "month" });
    }
  };

  const fetchData = async () => {
    if (!config || !config.sensors) return;

    setLoading(true);

    const allSensors = config.sensors.map((s) => s.key);

    if (allSensors.length === 0) {
      setChartData(initialChartData);
      setLoading(false);
      return;
    }

    const req: DataReqBody = {
      snr: device?.snr,
      from: formatDateToZ(date, false, filter.xFormat, device?.timezone),
      to: formatDateToZ(date, true, filter.xFormat, device?.timezone),
      bucket: filter.currentBucket,
      sensors: allSensors,
    };

    // Update state for date range
    setStartDate(formatDateToZ(date, false, filter.xFormat, device?.timezone));
    setEndDate(formatDateToZ(date, true, filter.xFormat, device?.timezone));

    const { data: newData, status, isLoading, error } = await dataMixed(req);
    setLoading(isLoading);

    if (!error && status && status === 200) {
      const labels: string[] = newData.map((element: ChartDataPoint) =>
        formatDate(
          filter.currentBucket === "hour" ? element.timestamp : element._id,
          filter.currentBucket
        )
      );
      setChartLabels(labels);
      const datasets: Dataset[] = [];

      config.sensors.forEach((sensor) => {
        const dataset: Dataset = {
          label: sensor.name,
          unit: sensor.unit,
          data: newData.map((element: ChartDataPoint) => {
            const rawValue = element[sensor.key];
            if (rawValue === undefined || rawValue === null) {
              return null;
            }
            return sensor.divisor ? rawValue / sensor.divisor : rawValue;
          }),
          backgroundColor: "transparent",
          borderColor: "transparent",
          borderWidth: 2,
          borderRadius: 5,
          borderSkipped: false,
          yAxisID: undefined,
          tension: 0.4,
          fill: sensor.fill,
          borderDash: [],
        };
        datasets.push(dataset);
      });

      setDataSets(datasets);

      const uniqueUnits = [
        ...new Set(datasets.map((ds) => ds.unit).filter(Boolean)),
      ] as string[];
      setAllUnits(uniqueUnits);

      const newSelectedLeftUnit =
        uniqueUnits.length > 0 ? uniqueUnits[0] : null;
      const newSelectedRightUnit =
        uniqueUnits.length > 1 ? uniqueUnits[1] : null;

      setSelectedLeftUnit(newSelectedLeftUnit);
      setSelectedRightUnit(newSelectedRightUnit);
    }
  };

  useEffect(() => {
    if (device?.snr && config) {
      fetchData();
    }
  }, [device, config, date, filter]);

  useEffect(() => {
    let colorIndex = 0;
    const finalDatasets = dataSets
      .filter(
        (ds) => ds.unit === selectedLeftUnit || ds.unit === selectedRightUnit
      )
      .map((ds) => {
        const isLeft = ds.unit === selectedLeftUnit;
        const color =
          colorPalette500Css[colorIndex % colorPalette500Css.length];
        if (ds.unit === selectedLeftUnit || ds.unit === selectedRightUnit) {
          colorIndex++;
        }

        return {
          ...ds,
          yAxisID: isLeft ? "y" : "y1",
          borderColor: color,
          backgroundColor: color,
          borderDash: isLeft ? [] : [5, 5],
          tension: isLeft ? 0.4 : 0.1,
        };
      })
      .sort((a, b) => (b.fill ?? false ? 0 : 1) - (a.fill ?? false ? 0 : 1));

    const newVisible: { [key: string]: boolean } = {};
    finalDatasets.forEach((ds) => {
      newVisible[ds.label] = visibleDatasets[ds.label] ?? true;
    });

    setChartData({
      labels: chartLabels,
      datasets: finalDatasets,
    });
    setVisibleDatasets(newVisible);
  }, [dataSets, selectedLeftUnit, selectedRightUnit, chartLabels]);

  const getDescription = () => {
    if (filter.calendar === "year") {
      return `${formatDate(startDate, "ddMMMyyyy")} - ${formatDate(
        endDate,
        "ddMMMyyyy"
      )}`;
    } else if (filter.calendar === "decade") {
      return `${formatDate(startDate, "MMMyyyy")} - ${formatDate(
        endDate,
        "MMMyyyy"
      )}`;
    } else if (filter.calendar === "month") {
      return `${formatDate(startDate, "ddMMMyyyy")}`;
    }
    return "";
  };

  const handleUnitChange = (unit: string, axis: "left" | "right") => {
    if (axis === "left") {
      setSelectedLeftUnit(unit);
    } else {
      setSelectedRightUnit(unit);
    }
  };

  const toggleDatasetVisibility = (label: string) => {
    setVisibleDatasets((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Filter datasets based on visibility
  const visibleDatasetsList = chartData.datasets.filter(
    (dataset) => visibleDatasets[dataset.label] !== false
  );

  const leftButtons = dataSets.filter((ds) => ds.unit === selectedLeftUnit);
  const rightButtons = dataSets.filter((ds) => ds.unit === selectedRightUnit);

  // Helper to strip phase prefix (L1, L2, L3) from sensor name
  const stripPhasePrefix = (name: string): string =>
    name.replace(/^L[123]\s+/i, "");

  // Helper to get unique unit options with display names (without phase prefix)
  const getUnitDisplayOptions = (): { id: string; name: string }[] => {
    if (!config?.sensors) return [];
    const seen = new Set<string>();
    return config.sensors
      .filter((sensor: { unit: string }) => {
        if (sensor.unit && !seen.has(sensor.unit)) {
          seen.add(sensor.unit);
          return true;
        }
        return false;
      })
      .map((sensor: { name: string; unit: string }) => ({
        id: sensor.unit,
        name: `${stripPhasePrefix(sensor.name)} - ${sensor.unit}`,
      }));
  };

  return (
    <DeviceCard
      title={t("chart.multi.line.title", {
        context: filter.calendar,
      })}
      subTitle={getDescription()}
      topRight={
        <>
          {TimeSeriesChartDropdown.map((item) => (
            <GenericButton
              key={item.value}
              text={item.label}
              onClick={() => setXFormat(item.value)}
              size="sm"
              color="50"
              variant={filter.calendar === item.value ? "solid" : "outline"}
              rounded="rounded-md"
              tooltip={t("tooltip.xformat.filter", { context: item.value })}
              tooltipPlacement="bottom"
            />
          ))}
          <DatePickerField
            id={"1"}
            sizes={"xs"}
            iconOnly
            value={date}
            onChange={(val) => {
              setDate(val);
            }}
            maxDate={new Date()}
            maxDetail={filter.calendar}
          />
        </>
      }
    >
      <div className="h-full min-h-72 w-full overflow-x-auto">
        {loading ? (
          <div className="mt-12 flex items-center justify-center bg-opacity-50">
            <PropagateLoader loading={loading} color="var(--color-500)" />
          </div>
        ) : Array.isArray(chartData.datasets) &&
          chartData.datasets.length > 0 &&
          chartData.datasets.some((ds) => ds.data.some((d) => d !== null)) ? (
          <>
            <div className="mb-4 flex gap-4">
              <div className="flex w-full flex-col gap-2">
                {allUnits.length > 0 && (
                  <Combobox
                    id="left-axis-unit-select"
                    placeholder=""
                    value={selectedLeftUnit}
                    onChange={(unit) => handleUnitChange(unit, "left")}
                    items={getUnitDisplayOptions().filter(
                      (option) => option.id !== selectedRightUnit
                    )}
                    sizes="sm"
                    rounded="rounded-md"
                    clearIcon={false}
                    labelKey="name"
                    valueKey="id"
                    extra="min-w-72 max-w-72"
                  />
                )}

                {leftButtons.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {leftButtons.map((dataset: Dataset) => {
                      const chartDataset = chartData.datasets.find(
                        (d) => d.label === dataset.label
                      );
                      return (
                        <ChartButton
                          key={dataset.label}
                          text={dataset.label}
                          size="xs"
                          onClick={() => toggleDatasetVisibility(dataset.label)}
                          color={
                            (chartDataset?.borderColor as string) || color0
                          }
                          extra={
                            visibleDatasets[dataset.label]
                              ? "flex items-center gap-2"
                              : ""
                          }
                        >
                          {visibleDatasets[dataset.label] && (
                            <FaCheck className="text-xs" />
                          )}
                        </ChartButton>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex w-full flex-col items-end gap-2">
                {allUnits.length > 0 && (
                  <Combobox
                    id="right-axis-unit-select"
                    placeholder=""
                    value={selectedRightUnit}
                    onChange={(unit) => handleUnitChange(unit, "right")}
                    items={getUnitDisplayOptions().filter(
                      (option) => option.id !== selectedLeftUnit
                    )}
                    sizes="sm"
                    rounded="rounded-md"
                    clearIcon={false}
                    labelKey="name"
                    valueKey="id"
                    extra="min-w-72 max-w-72"
                  />
                )}

                {rightButtons.length > 0 && (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {rightButtons.map((dataset: Dataset) => {
                      const chartDataset = chartData.datasets.find(
                        (d) => d.label === dataset.label
                      );
                      return (
                        <ChartButton
                          key={dataset.label}
                          text={dataset.label}
                          size="xs"
                          onClick={() => toggleDatasetVisibility(dataset.label)}
                          color={
                            (chartDataset?.borderColor as string) || color1
                          }
                          extra={`${
                            visibleDatasets[dataset.label]
                              ? "flex items-center gap-2 "
                              : ""
                          }border-dashed`}
                        >
                          {visibleDatasets[dataset.label] && (
                            <FaCheck className="text-xs" />
                          )}
                        </ChartButton>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <LineChart
              id={"chartId"}
              data={{
                ...chartData,
                datasets: visibleDatasetsList,
              }}
              config={config}
              height={height}
            />
          </>
        ) : (
          <div className="mt-4 flex justify-center">
            <img src={NoDataSvg} alt="Modal content" style={{ width: "12%" }} />
          </div>
        )}
      </div>
    </DeviceCard>
  );
};

export default MultiLine;
